import { useTranslation } from 'react-i18next';
import { Icon, Typography, Button } from '@/components';
import { useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';
import { customRound, displayQuarter, europeanFormatNumber } from '@/helpers';
import { DatacenterItem } from '@/features/projects/types';
import { useDataCenterType } from '@/features/projects/hooks/use-datacenter-type-options';
import { useRedundancy } from '@/features/projects/hooks/use-redundancy-options';
import { useCoolingTechnology } from '@/features/projects/hooks/use-cooling-technology-options';

export type DatacenterBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function DatacenterBox({ normalizedProject, isEditable, onEdit }: DatacenterBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const { map: mapDataCenterType } = useDataCenterType();
  const { map: mapRedundancy } = useRedundancy();
  const { map: mapCoolingTechnology } = useCoolingTechnology();
  const datacenterTech = normalizedProject.technologies.find((tech: any) => tech.type === 'datacenter');
  if (!datacenterTech) return null;

  const item: DatacenterItem = datacenterTech.details.item;
  const status = datacenterTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const roundedInstalledCapacity = item.installedCapacity && customRound(item.installedCapacity);
  const roundedItCapacity = item.itCapacity && customRound(item.itCapacity);

  const fields = [
    {
      label: t('Grid Capacity'),
      value: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    },
    { label: t('Type of Project'), value: item.typeProject ?? null },
    { label: t('Development Status'), value: mapDevelopmentStatus(status) || null },
    { label: t('Ready to Build (RtB) Date'), value: item.readyToBuild ? displayQuarter(item.readyToBuild) : null },
    {
      label: t('IT Capacity'),
      value: item.itCapacity ? `${europeanFormatNumber(roundedItCapacity)} MW` : null,
    },
    { label: t('PUE'), value: item.pue ?? null },
    { label: t('Data Center Type'), value: mapDataCenterType(item.dataCenterType) ?? null },
    { label: t('Redundancy'), value: mapRedundancy(item.redundancy) ?? null },
    { label: t('Cooling technology '), value: mapCoolingTechnology(item.coolingTechnology) ?? null },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="datacenter" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Data Center Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.generalComments) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('Other Comments')}:{' '}
          <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
            {item.generalComments ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
